import React, { useRef } from "react";
import Icon from "../common/Icon.jsx";
import Button from "../common/Button.jsx";
import { fonts } from "../common/mixins.js";
import styled, { createGlobalStyle } from "styled-components";
import { formatDate } from "../common/helpers.js";
import useMediaQuery from "@mui/material/useMediaQuery";
import ReservationAlert from "./ReservationAlert.jsx";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";

const PrintStyles = createGlobalStyle`
  /* Force A4 portrait, zero margins so clipping is precise */
  @page {
    size: A4 portrait;
    margin: 0;
  }

  @media print {
    /* Remove every box- and text-shadow */
    *, *::before, *::after {
      box-shadow: none !important;
      text-shadow: none !important;
    }
      .MuiTypography-root.MuiTypography-h6 {
        display: none;
      }
  }
`;

// Styled components (no changes here)
const Header = styled.div`
  ${fonts.title()};
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TitleSmall = styled.div`
  ${fonts.normalTextBold()};
  margin-bottom: 8px;
`;

const InfoContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #edf1f6;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 16px;
`;

const Small = styled.div`
  ${fonts.normalText()};
  display: inline;
`;

const StyledImage = styled.img`
  width: 255px;
  height: 47.6px;
  @media print {
    display: block !important;
    /* bump the width up when printing… */
    width: 380px !important;
    /* …and let the height auto-adjust to preserve aspect-ratio */
    height: auto !important;
  }
`;

const PrintLogo = styled.div`
  text-align: center;
  display: none;
  @media print {
    display: block !important;
  }
`;

const Number = styled.div`
  ${fonts.normalTextSmall()};
`;

const Icons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: 16px;
`;

const CountIcon = styled.div`
  font-size: 14px;
  font-family: "pf_highway_sans_pro_lightRg";
  display: flex;
  gap: 4px;
  align-items: center;
  line-height: 16px;
`;

const Amount = styled.div`
  font-family: "pf_highway_sans_pro_lightRg";
  border: 1px solid #fcb041;
  background: #fff7ec;
  border-radius: 8px;
  color: #0d0c1c;
  font-size: 16px;
  padding: 8px 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const PaymentCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 4px;
  align-items: center;
  margin-bottom: 0;
`;

const IconContainer = styled.div`
  flex-shrink: 0;
`;

const DateTimeSmall = styled.div`
  ${fonts.normalTextSmall()};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PayWithCashModal = ({
  paymentCode,
  dateLocation,
  eventName,
  expirationDate,
  culture,
  totalTickets,
  totalProducts,
  outstandingAmount,
  closeModal,
}) => {
  const isInDrawer = useMediaQuery("(max-width:900px)");

  const printRef = useRef();

  const handlePrint = async (e) => {
    e.preventDefault();

    const element = printRef.current;
    if (!element) return;

    const container = document.createElement("div");
    Object.assign(container.style, {
      position: "absolute",
      width: element.offsetWidth + "px", // match your modal width
    });
    document.body.appendChild(container);

    try {
      const clone = element.cloneNode(true);
      container.appendChild(clone);

      clone.style.backgroundColor = "#fff";
      clone.style.padding = "16px 16px 24px 16px";
      clone.style.display = "block";

      const logoInClone = container.querySelector("#printLogo");
      const dateIconInClone = container.querySelector("#dateIcon");
      const ticketIconInClone = container.querySelector("#ticketIcon");

      if (logoInClone) logoInClone.style.display = "block";
      if (dateIconInClone) dateIconInClone.style.display = "block";
      if (ticketIconInClone) ticketIconInClone.style.display = "block";

      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (false) { //isIOS || isSafari || isInDrawer) {
        const canvas = await html2canvas(clone, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          onclone: (clonedDoc) => {
            clonedDoc.querySelectorAll("i").forEach((iconEl) => {
              const bg = getComputedStyle(iconEl).backgroundImage;
              const match = /url\(["']?(.*?)["']?\)/.exec(bg);
              if (!match) return;
              const url = match[1];

              const img = document.createElement("img");
              img.src = url;
              img.crossOrigin = "anonymous";
              img.width = iconEl.offsetWidth;
              img.height = iconEl.offsetHeight;
              img.style.cssText = getComputedStyle(iconEl).cssText;

              iconEl.replaceWith(img);
            });
          },
        });
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              alert("Failed to generate image.");
              document.body.removeChild(container);
              return;
            }

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `MORE_${paymentCode}.jpeg`;
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
          },
          "image/jpeg",
          1.0
        );
      } else {
          const opt = {
              margin: 0,
              filename: `MORE_${paymentCode}.pdf`,
              localUrlAccess: true,
              image: { type: "jpeg", quality: 1 },
              html2canvas: { scale: 1, useCORS: true, allowTaint: true },
              jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
          };

          const pdfBlob = await html2pdf().set(opt).from(clone).outputPdf("blob");
          const blobUrl = URL.createObjectURL(pdfBlob);

          if (window.location.host.includes('moreapp')) {
              // Automatically download the PDF    
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = opt.filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(blobUrl);
          } else {
              // Open in new tab and optionally print
              const win = window.open(blobUrl, "_blank");

              if (!win) {
                  alert("Please allow popups to view and print the PDF.");
              } else {
                  win.onload = () => win.print?.();
              }
          }

      }
    } finally {
      document.body.removeChild(container);
    }
  };

  const { date, customDate, customTime, location } = dateLocation;

  const formattedDate = formatDate({
    date,
    culture,
    customDate,
    customTime,
  });

  const getBaseUrl = () => {
    const host = window.location.hostname;
    let prefix = "travel";
    if (host.includes("dev")) prefix = "dev-travel";
    else if (host.includes("preview")) prefix = "preview-travel";
    return `https://${prefix}.more.com/cmstemplates/vivatravelaspx/data/common/barcode.ashx?linewidth=7&fontsize=30&height=140&hidetext=true&code=`;
  };
  const baseUrl = getBaseUrl();

  return (
    <>
      <PrintStyles />
      <div style={{ margin: "0 auto", borderRadius: 8 }}>
        <div ref={printRef}>
          <PrintLogo id="printLogo">
            <img
              src="/Site/images/logos/more.svg"
              style={{ width: 185 }}
              alt="logo"
            />
            <div
              style={{
                fontWeight: 600,
                fontSize: 20,
                marginBottom: 24,
                textAlign: "left",
              }}
            >
              {orderdetails_resources.CashPaymentDetails}
            </div>
          </PrintLogo>

          <Header>
            <TitleContainer>
              <Amount>
                <div>{orderdetails_resources.OutstandingAmount}</div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                  {outstandingAmount}
                </div>
              </Amount>
              <PaymentCodeContainer>
                <Small>{orderdetails_resources.PaymentCodeNotForDeposit}</Small>{" "}
                <Small style={{ fontWeight: 600, fontSize: 16 }}>
                  {paymentCode}
                </Small>
              </PaymentCodeContainer>
            </TitleContainer>
          </Header>

          <InfoContainer>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <TitleSmall>{eventName}</TitleSmall>
              <Icons>
                {totalTickets > 0 && (
                  <CountIcon>
                    <div style={{ color: "#703EFF" }}>{totalTickets}</div>
                    <Icon
                      id="ticketIcon"
                      kind={totalTickets === 1 ? "ticket_purple" : "tickets"}
                    />
                  </CountIcon>
                )}
                {totalProducts > 0 && (
                  <CountIcon>
                    <div style={{ color: "#703EFF" }}>{totalProducts}</div>
                    <Icon kind="product" />
                  </CountIcon>
                )}
              </Icons>
            </div>
            <FlexContainer>
              <IconContainer>
                <Icon id="dateIcon" kind="date" />
              </IconContainer>
              <DateTimeSmall id="date">{formattedDate}</DateTimeSmall>
            </FlexContainer>
            <FlexContainer>
              <IconContainer>
                {" "}
                <Icon id="locationIcon" kind="location" />{" "}
              </IconContainer>
              <DateTimeSmall id="location">{location}</DateTimeSmall>
            </FlexContainer>
          </InfoContainer>

          <ReservationAlert expirationDate={expirationDate} culture={culture} />

          <Container>
            <div
              style={{
                fontSize: 14,
                fontWeight: 400,
                fontFamily: "pf_highway_sans_pro_lightRg",
                marginBottom: 8,
              }}
            >
              {orderdetails_resources.NotAnActualTicket}
            </div>
            <StyledImage
              src={`${baseUrl}${paymentCode}`}
              alt="barcode"
              crossOrigin="anonymous"
            />
            <Number>{paymentCode}</Number>
          </Container>
        </div>
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 24 }}
        >
          <Button
            type="primary"
            icon="print"
            width={isInDrawer && "100%"}
            text={
              isInDrawer
                ? orderdetails_resources.DownloadTickets
                : orderdetails_resources.Print
            }
            onClick={handlePrint}
          />
        </div>
      </div>
    </>
  );
};

export default PayWithCashModal;
